import React, { useState, useRef, useEffect } from 'react'
import { RiArrowDownSLine, RiCheckLine, RiSearchLine } from 'react-icons/ri'
import './PhoneInput.css'

interface Country {
    code: string
    name: string
    dial_code: string
}

interface PhoneInputProps {
    label?: string
    value: string // Full phone number with country code e.g., "+51999888777"
    setValue: (value: string) => void
    isRequired?: boolean
    error?: string
    disabled?: boolean
}

// Comprehensive list of countries with their dial codes
const countries: Country[] = [
    { code: 'PE', name: 'Perú', dial_code: '+51' },
    { code: 'US', name: 'United States', dial_code: '+1' },
    { code: 'AR', name: 'Argentina', dial_code: '+54' },
    { code: 'BO', name: 'Bolivia', dial_code: '+591' },
    { code: 'BR', name: 'Brasil', dial_code: '+55' },
    { code: 'CL', name: 'Chile', dial_code: '+56' },
    { code: 'CO', name: 'Colombia', dial_code: '+57' },
    { code: 'CR', name: 'Costa Rica', dial_code: '+506' },
    { code: 'CU', name: 'Cuba', dial_code: '+53' },
    { code: 'DO', name: 'República Dominicana', dial_code: '+1' },
    { code: 'EC', name: 'Ecuador', dial_code: '+593' },
    { code: 'SV', name: 'El Salvador', dial_code: '+503' },
    { code: 'GT', name: 'Guatemala', dial_code: '+502' },
    { code: 'HN', name: 'Honduras', dial_code: '+504' },
    { code: 'MX', name: 'México', dial_code: '+52' },
    { code: 'NI', name: 'Nicaragua', dial_code: '+505' },
    { code: 'PA', name: 'Panamá', dial_code: '+507' },
    { code: 'PY', name: 'Paraguay', dial_code: '+595' },
    { code: 'UY', name: 'Uruguay', dial_code: '+598' },
    { code: 'VE', name: 'Venezuela', dial_code: '+58' },
    { code: 'ES', name: 'España', dial_code: '+34' },
    { code: 'GB', name: 'United Kingdom', dial_code: '+44' },
    { code: 'FR', name: 'Francia', dial_code: '+33' },
    { code: 'DE', name: 'Alemania', dial_code: '+49' },
    { code: 'IT', name: 'Italia', dial_code: '+39' },
    { code: 'CN', name: 'China', dial_code: '+86' },
    { code: 'JP', name: 'Japón', dial_code: '+81' },
    { code: 'KR', name: 'Corea del Sur', dial_code: '+82' },
    { code: 'IN', name: 'India', dial_code: '+91' },
    { code: 'AU', name: 'Australia', dial_code: '+61' },
    { code: 'CA', name: 'Canadá', dial_code: '+1' },
]

const PhoneInput = ({
    label,
    value,
    setValue,
    isRequired = false,
    error,
    disabled = false,
}: PhoneInputProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedCountryCode, setSelectedCountryCode] = useState('+51')
    const [phoneNumber, setPhoneNumber] = useState('')
    const dropdownRef = useRef<HTMLDivElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)

    // Parse the value to extract country code and phone number
    useEffect(() => {
        if (value) {
            // Find the matching country code
            const matchingCountry = countries.find(country => 
                value.startsWith(country.dial_code)
            )
            if (matchingCountry) {
                setSelectedCountryCode(matchingCountry.dial_code)
                setPhoneNumber(value.slice(matchingCountry.dial_code.length))
            }
        } else {
            setSelectedCountryCode('+51') // Default to Peru
            setPhoneNumber('')
        }
    }, [value])

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false)
                setSearchTerm('')
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus()
        }
    }, [isOpen])

    // Filter countries based on search term
    const filteredCountries = countries.filter(country => {
        const search = searchTerm.toLowerCase()
        return (
            country.name.toLowerCase().includes(search) ||
            country.dial_code.includes(search) ||
            country.code.toLowerCase().includes(search)
        )
    })

    const handleCountrySelect = (dialCode: string) => {
        setSelectedCountryCode(dialCode)
        setIsOpen(false)
        setSearchTerm('')
        // Update the full value
        const fullNumber = dialCode + phoneNumber
        setValue(fullNumber)
    }

    const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newNumber = e.target.value.replace(/[^0-9]/g, '') // Only allow numbers
        setPhoneNumber(newNumber)
        // Update the full value
        const fullNumber = selectedCountryCode + newNumber
        setValue(fullNumber)
    }

    const selectedCountry = countries.find(c => c.dial_code === selectedCountryCode)

    return (
        <div className="phone-input-group">
            {label && (
                <label className="phone-input-label">
                    {label}
                    {isRequired && <span className="required-mark">*</span>}
                </label>
            )}
            
            <div className="phone-input-wrapper">
                {/* Country Code Select */}
                <div 
                    ref={dropdownRef}
                    className={`phone-country-select ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
                >
                    <div 
                        className="phone-country-trigger"
                        onClick={() => !disabled && setIsOpen(!isOpen)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                if (!disabled) setIsOpen(!isOpen)
                            }
                        }}
                        tabIndex={disabled ? -1 : 0}
                        role="button"
                        aria-haspopup="listbox"
                        aria-expanded={isOpen}
                    >
                        <span className="phone-country-value">
                            {selectedCountry?.dial_code || '+51'}
                        </span>
                        <RiArrowDownSLine className={`phone-country-arrow ${isOpen ? 'rotated' : ''}`} />
                    </div>
                    
                    {isOpen && (
                        <div className="phone-country-dropdown">
                            <div className="phone-country-search">
                                <RiSearchLine className="phone-search-icon" />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    className="phone-search-input"
                                    placeholder="Buscar país o código..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                            <div className="phone-country-options" role="listbox">
                                {filteredCountries.length > 0 ? (
                                    filteredCountries.map((country) => (
                                        <div
                                            key={country.code}
                                            className={`phone-country-option ${selectedCountryCode === country.dial_code ? 'selected' : ''}`}
                                            onClick={() => handleCountrySelect(country.dial_code)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault()
                                                    handleCountrySelect(country.dial_code)
                                                }
                                            }}
                                            tabIndex={0}
                                            role="option"
                                            aria-selected={selectedCountryCode === country.dial_code}
                                        >
                                            <span className="phone-country-name">{country.name}</span>
                                            <span className="phone-country-code">{country.dial_code}</span>
                                            {selectedCountryCode === country.dial_code && (
                                                <RiCheckLine className="phone-option-check" />
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <div className="phone-no-results">No se encontraron resultados</div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Phone Number Input */}
                <input
                    type="tel"
                    className="phone-number-input"
                    placeholder="999 999 999"
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                    disabled={disabled}
                    aria-invalid={!!error}
                />
            </div>

            {error && <span className="phone-input-error">{error}</span>}
        </div>
    )
}

export default PhoneInput
